// utils/semanticColors.js
//
// THE single source of truth for Chariot's platform-wide semantic color system.
// Color carries meaning consistently everywhere it appears: order tags, workflow
// statuses, and (via the linked order's status) calendar appointments.
//
// 7 families, each = ONE meaning, with a vivid + light primary and a canonical
// icon (from utils/tagIcons). Vivid = active/now/primary; light = the same
// meaning, quieter (badge backgrounds, tentative/upcoming).
//
// Surfaces:
//  - Order tags  → SemanticColorPicker shows COLOR_SECTIONS (a few hues per family)
//  - Workflow statuses → same picker, ENFORCED to the palette, defaulted by type
//  - Appointments → inherit the linked order's workflow-status color
//  - Legend      → ColorLegend renders SEMANTIC_FAMILIES

// Canonical family = the one color + icon that represents each meaning.
// Ordered Info → Working → At-risk → Critical → Ready → Internal → Inactive.
// Info's canonical is the platform brand color (#0891b2 = --primary).
export const SEMANTIC_FAMILIES = [
  { key: "blue",   label: "Info",     meaning: "Neutral / default / informational",            icon: "info",    vivid: "#0891b2", light: "#cffafe" },
  { key: "yellow", label: "Working",  meaning: "Actively working — prep, parts, scheduling",    icon: "wrench",  vivid: "#eab308", light: "#fef08a" },
  { key: "orange", label: "At risk",  meaning: "Needs attention — overdue or waiting",          icon: "clock",   vivid: "#f97316", light: "#fed7aa" },
  { key: "red",    label: "Critical", meaning: "Emergency or blocked — stop / intervene",       icon: "alert",   vivid: "#ef4444", light: "#fecaca" },
  { key: "green",  label: "Ready / Complete",    meaning: "Ready to go — approved, all clear",            icon: "check",   vivid: "#22c55e", light: "#bbf7d0" },
  { key: "purple", label: "Internal", meaning: "Staff & internal — PTO, training, meetings",    icon: "user",    vivid: "#8b5cf6", light: "#ddd6fe" },
  { key: "gray",   label: "Inactive", meaning: "Done & filed — completed, canceled, archived",  icon: "archive", vivid: "#6b7280", light: "#e5e7eb" },
];

// A few hues per family (vivid row + matching light row), for the picker so tags
// can vary visually WITHIN a meaning. The first vivid of each family is its
// canonical color (above).
export const COLOR_SECTIONS = [
  // Info first; the brand color (#0891b2 = --primary) leads the blues and is the
  // default for new tags. Blue/sky/cyan only — no indigo (it reads as purple).
  { key: "blue",   label: "Info",     icon: "info",
    hint: "Neutral, default, and informational — standard statuses and tags that don't signal urgency or action.",
    vivid: [{ name: "Brand", color: "#0891b2" }, { name: "Blue", color: "#3b82f6" }, { name: "Sky", color: "#0ea5e9" }, { name: "Cyan", color: "#06b6d4" }],
    light: [{ name: "Light Cyan", color: "#cffafe" }, { name: "Light Blue", color: "#bfdbfe" }, { name: "Light Sky", color: "#bae6fd" }, { name: "Pale Cyan", color: "#a5f3fc" }] },
  // Bright lemon yellows — kept clearly distinct from the oranges below.
  { key: "yellow", label: "Working",  icon: "wrench",
    hint: "Actively being worked — prep, ordering parts, scheduling. The job is in motion and on track.",
    vivid: [{ name: "Yellow", color: "#facc15" }, { name: "Gold", color: "#eab308" }, { name: "Mustard", color: "#ca8a04" }], light: [{ name: "Light Yellow", color: "#fef9c3" }, { name: "Pale Yellow", color: "#fef08a" }] },
  // True oranges (no amber) so the family reads as orange, not gold.
  { key: "orange", label: "At risk",  icon: "clock",
    hint: "Needs attention — overdue, waiting on something, or at risk of slipping. Not blocked, but watch it.",
    vivid: [{ name: "Amber", color: "#fb923c" }, { name: "Orange", color: "#f97316" }, { name: "Deep Orange", color: "#ea580c" }], light: [{ name: "Light Orange", color: "#fed7aa" }, { name: "Pale Orange", color: "#ffedd5" }] },
  { key: "red",    label: "Critical", icon: "alert",
    hint: "Emergency, blocked, or a hard stop — needs immediate intervention before work can continue.",
    vivid: [{ name: "Red", color: "#ef4444" }, { name: "Rose", color: "#f43f5e" }, { name: "Crimson", color: "#b91c1c" }], light: [{ name: "Light Red", color: "#fecaca" }, { name: "Light Rose", color: "#fecdd3" }] },
  { key: "green",  label: "Ready / Complete",    icon: "check",
    hint: "Cleared to go — approved, all clear, ready for the next step.",
    vivid: [{ name: "Green", color: "#22c55e" }, { name: "Emerald", color: "#10b981" }, { name: "Lime", color: "#84cc16" }], light: [{ name: "Light Green", color: "#bbf7d0" }, { name: "Light Emerald", color: "#a7f3d0" }, { name: "Light Lime", color: "#d9f99d" }] },
  { key: "purple", label: "Internal", icon: "user",
    hint: "Staff and internal items — PTO, training, meetings. Not customer work.",
    vivid: [{ name: "Violet", color: "#8b5cf6" }, { name: "Purple", color: "#a855f7" }, { name: "Fuchsia", color: "#d946ef" }], light: [{ name: "Light Violet", color: "#ddd6fe" }, { name: "Light Purple", color: "#e9d5ff" }, { name: "Light Fuchsia", color: "#f5d0fe" }] },
  { key: "gray",   label: "Inactive", icon: "archive",
    hint: "Done and filed away — completed, canceled, or archived. No action needed.",
    vivid: [{ name: "Gray", color: "#6b7280" }, { name: "Slate", color: "#64748b" }, { name: "Dark Slate", color: "#475569" }], light: [{ name: "Light Gray", color: "#e5e7eb" }, { name: "Light Slate", color: "#e2e8f0" }] },
];

// Smart default color for an enforced workflow status, from its order_status_type.
export const STATUS_TYPE_FAMILY = {
  estimate: "blue",
  repair_order: "yellow",
  invoice: "green",
  archive: "gray",
};

export const familyByKey = (key) =>
  SEMANTIC_FAMILIES.find((f) => f.key === key) || null;

// Smart default vivid color for an order_status_type (Blue if unknown/none).
export const defaultColorForStatusType = (type) =>
  (familyByKey(STATUS_TYPE_FAMILY[type] || "blue") || familyByKey("blue")).vivid;

// Every hex the palette allows (vivid + light across all families) — for
// validation/enforcement.
export const ALL_SEMANTIC_HEXES = COLOR_SECTIONS.flatMap((s) =>
  [...s.vivid, ...s.light].map((c) => c.color.toLowerCase()),
);

export const isSemanticColor = (hex) =>
  typeof hex === "string" && ALL_SEMANTIC_HEXES.includes(hex.toLowerCase());

// ---- Nearest-family snapping (for migrating legacy free-hex colors) ----
const hexToRgb = (hex) => {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) return null;
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
};

// Returns the SEMANTIC family whose vivid color is closest to the given hex.
export const nearestFamily = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return familyByKey("blue"); // blue/default
  let best = SEMANTIC_FAMILIES[0];
  let bestDist = Infinity;
  for (const f of SEMANTIC_FAMILIES) {
    const frgb = hexToRgb(f.vivid);
    const d = (rgb[0] - frgb[0]) ** 2 + (rgb[1] - frgb[1]) ** 2 + (rgb[2] - frgb[2]) ** 2;
    if (d < bestDist) { bestDist = d; best = f; }
  }
  return best;
};

// Given any stored hex, return the family it belongs to (exact match preferred,
// else nearest) — used by the legend/badges to label a color's meaning.
export const familyForColor = (hex) => {
  if (!hex) return null;
  const lower = hex.toLowerCase();
  for (const f of SEMANTIC_FAMILIES) {
    if (f.vivid.toLowerCase() === lower || f.light.toLowerCase() === lower) return f;
  }
  // Fall back to the section variants, then nearest.
  for (const s of COLOR_SECTIONS) {
    if ([...s.vivid, ...s.light].some((c) => c.color.toLowerCase() === lower)) {
      return familyByKey(s.key);
    }
  }
  return nearestFamily(hex);
};
